import React, { useState, useRef, useEffect, memo, Suspense, useCallback } from 'react';
import * as THREE from 'three';
import MLeg from '../marker/mLeg';
import MLine from '../marker/mLine';
import MProjectCurve from '../marker/mProjectCurve';
import MPointFix from '../marker/mPointFix';
import MLineMove from '../marker/mLineMove';
import { MUpperCurve, MUpperSurface, MUpperSurfaceSi, MUpperSurfaceMl, MUpperSurfaceAp } from '../marker/mUpperAk';
import MTest from '../marker/mTest';
import MMesh from '../marker/mMesh';
import { MExtrudeSurface, MExtrudeSurfaceWithPointIdx } from '../marker/mExtrudeSurface';
import MMeasure from '../marker/mMeasure';

import { Lines } from "../shape/Line";
import { CrossSectionsAkScan, CrossSectionsAk, CrossSectionAkText, CrossSectionAkScanText } from '../marker/mCrossSection';

import MPoints2DAdd from '../marker/mPoints2DAdd';
import MPoints2DDel from '../marker/mPoints2DDel';
import MPoints3D from '../marker/mPoints3D';
import MAssembleAk from '../marker/mAssembleAk';
// import MOutputAk from '../marker/mOutputAk';
import MOutputAkFd from '../marker/mOutputAkFd';
import MTrim3D from '../marker/mTrim3D';

import MBox from '../marker/mBox';
import MSocket from '../marker/mSocket';

import { BaseShape } from '../shape/BaseShape';
import { GetBounds } from '../shape/Compress';
import Control from '../store/control';
import Param from '../store/param';

import { LoadSTL } from '../fileIO/fileLoader';


// -----------------------------------------------
// (final and test socket) common marker control
// Access to Param should be centralized here and not under marker/!
// -----------------------------------------------

/**
 * This module contains the MarkingCommon component which is used in the Instalimb Modeler frontend.
 * It retrieves various parameters and controls from the Param and Control modules respectively, and uses them to determine which mesh to display.
 * It also sets various flags to determine which components to display.
 */
const MarkingCommon = () => {
  const activeStep         = Param.Get("activeStep");
  const setup              = Param.Get("setup");
  const align              = Param.Get("align");
  const comp               = Param.Get("compress");
  const trim               = Param.Get("trim");
  const trim1              = Param.Get("trim1");
  const trim2              = Param.Get("trim2");
  const thick              = Param.Get("thick");
  const joint              = Param.Get("joint");
  const assemble           = Param.Get("assemble");
  const assembleAk           = Param.Get("assembleAk");
  const reinforth          = Param.Get("reinforth");
  const connection         = Param.Get("connection");
  const output             = Param.Get('output');
  const oc                 = Param.Get('oc');
  const setParam           = Param.Get("setParam");

  // AK
  const baseshape          = Param.Get("baseshape");
  const setBaseshapePoints = Param.Get("setBaseshapePoints");


  //const debug              = Param.Get("debug");
  const setControl         = Control.Get("setControl");
  const showLeg            = Control.Get("showLeg");
  const showSocket         = Control.Get("showSocket");
  const showTrimSurface    = Control.Get("showTrimSurface");
  const wireLeg            = Control.Get("wireLeg");
  const transparentLeg     = Control.Get("transparentLeg");
  const redrawBox          = Control.Get("redrawBox");
  const wireSocket         = Control.Get("wireSocket");
  const meshLeg            = Control.Get("meshLeg");
  const meshLegAligned     = Control.Get("meshLegAligned"); // after alignment. When we get crossSections, we need to store aligned leg data
  const meshSocketCompress = Control.Get("meshSocketCompress");
  const meshSocketModify   = Control.Get("meshSocketModify");
  const meshSocketTrim     = Control.Get("meshSocketTrim");
  const meshSocketThick    = Control.Get("meshSocketThick");
  const meshSocketJoint    = Control.Get("meshSocketJoint");

  // AK
  const meshTrim1LowerSurface = Control.Get("meshTrim1LowerSurface");
  const meshTrim2UpperSurface = Control.Get("meshTrim2UpperSurface");
  const meshTrim2UpperSurfaceSi = Control.Get("meshTrim2UpperSurfaceSi");
  const meshTrim2UpperSurfaceMl = Control.Get("meshTrim2UpperSurfaceMl");
  const meshTrim2UpperSurfaceAp = Control.Get("meshTrim2UpperSurfaceAp");
  const meshAssembleLowerSurface = Control.Get("meshAssembleLowerSurface");
  const meshAssembleUpperSurface = Control.Get("meshAssembleUpperSurface");
  

  const meshFileFoot = Control.Get('meshFileFoot');
  const meshFileKnee = Control.Get('meshFileKnee');
  const meshFoot     = Control.Get('meshFoot');
  const meshKnee     = Control.Get('meshKnee');

  const flgLegClickable    = Control.Get("flgLegClickable");
  const flgSocketClickable = Control.Get("flgSocketClickable");
  const currentAngle       = Control.Get("currentAngle");
  const showBound          = Control.Get("showBound");
  const showLine           = Control.Get("showLine");
  const pointCross         = Control.Get("pointCross");
  const pointMerdian       = Control.Get("pointMerdian");
  const type               = Control.Get("type");
  const measure            = Control.Get('measure')


  // console.log('marking common');
  // console.log(activeStep);
  var meshSocketShow = null;
  var opacity = 0;
  showLeg? opacity = 0.6 : opacity = 0.9;

  // load mesh foot and knee
  useEffect(() => {
    if (meshFoot==null) {
      const promises = [
        LoadSTL(meshFileFoot, 'PHONG'),
      ];
      Promise.all(promises).then(result => {
        var temp = result[0];
        temp.geometry.rotateX(-Math.PI / 2, 0, 0);
        setControl(s => {s.meshFoot = temp});
      });
    };
  
    if (meshKnee==null) {
      const promises = [
        LoadSTL(meshFileKnee, 'PHONG'),
      ];
      Promise.all(promises).then(result => {
        var temp = result[0];
        temp.geometry.rotateX(-Math.PI / 2, 0, 0);
        setControl(s => {s.meshKnee = temp});
      });
    };
  }, []);


  // save aligned mesh leg for output at the final step of alignment
  if (activeStep == 1 && align.step == 3) {
    // deepCopy
    let leg = meshLeg.clone()
    let geometry = leg.geometry.clone()
    let material = leg.material.clone()
    const alignedMeshLeg = new THREE.Mesh(geometry, material);
    setControl(s => {s.alignedMeshLeg = alignedMeshLeg});
  }
  
  /*
  The switch statement sets the meshSocketShow variable based on the current step. 
  The meshSocketShow variable is used to determine which mesh to display on the screen.
  Overall, this code block is used to manage the display of different meshes based on the current step in the user's workflow.
  */
  switch(activeStep){
    case 3:  // compress
      meshSocketShow = meshSocketCompress;
      break;
    case 4:  // Modify
      meshSocketShow = meshSocketModify;
      break;
    case 5:  // Trim
      meshSocketShow = (trim.step < 2) ? meshSocketModify : meshSocketTrim; // trim.step 0:before apply 1:after apply
      break;
    case 6:  // Thick
      meshSocketShow = (thick.step == 0) ? meshSocketTrim : meshSocketThick;
      break;
    case 7:  // Output
      meshSocketShow = meshSocketThick;
      break;
  }
  

  //const isAxis      = (activeStep <= 8);
  const isMeasure   = measure;
  const isLeg       = meshLeg && showLeg;
  //TODO: take care of temporary implementation of meshsocketcompress
  const isSocket    = meshSocketCompress && showSocket && (activeStep >= 3);
  const isIschium   = (activeStep == 1) && (align.step == 1); // Ischium level
  const isCenterX   = (activeStep == 1) && (align.step == 2); // Bottom length
  const isCenterY   = (activeStep == 1) && (align.step == 3); // Greater trochanter
  const isBaseShape = (activeStep === 2);
  const isComp      = (activeStep === 3);
  const isCrossCurve = (activeStep === 3) || (activeStep === 4) || (activeStep === 5);
  const isTrim1 = (activeStep === 4);
  // only for AK
  const isTrim2 = (activeStep === 5);
  const isTrim2Si = (activeStep === 5) && (trim2.step === 0);
  const isTrim2Ml = (activeStep === 5) && (trim2.step === 1);
  const isTrim2Ap = (activeStep === 5) && (trim2.step === 2);
  const isAssemble = (activeStep === 6) //|| (activeStep === 7);
  const isOutput = (activeStep === 7) && (type === 'TestSocket');
  const isOutputFd = (activeStep === 7) && (type === 'FinalDevice');


  // for marking
  var meshSocket = null;
  if ( meshSocketCompress ) {
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.MeshLambertMaterial();
    geometry.setFromPoints(meshSocketCompress.vertices);
    geometry.setIndex(meshSocketCompress.faces)
    geometry.rotateX(-Math.PI / 2,0,0)
    meshSocket = new THREE.Mesh(geometry, material);
    //meshSocket.rotation.x = -Math.PI / 2,0,0
  }

  if (meshSocketJoint) {
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.MeshLambertMaterial();
    geometry.setFromPoints(meshSocketCompress.vertices);
    geometry.setIndex(meshSocketCompress.faces)
    geometry.rotateX(-Math.PI / 2,0,0)
    meshSocket = new THREE.Mesh(geometry, material);
    //meshSocket.rotation.x = -Math.PI / 2,0,0
  }

  const callSavePointMarkAk = (v) => {
    if((currentAngle >= 1) && (currentAngle <= 6)){
      setParam(s => {
        s.align.greaterTrochanter = v
      });
    }
    else
      console.warn("Can't mark in Free View");
  }
  const callSaveStumpLength = (v) => {
    setParam(s => {s.align.stumpLength = v});
  }

  const callSaveStumpLengthX = (v) => {
    setParam(s => {s.align.stumpLengthX = v});
  }

  const callSaveLegCenter = useCallback((center) => {
    setParam(s => {s.align.legCenter = center});
  }, []);
  const callSaveLegRotation = useCallback((angle) => {
    setParam(s => {s.align.rotationX = angle[0]});
    setParam(s => {s.align.rotationY = angle[1]});
    setParam(s => {s.align.rotationZ = angle[2]});
  }, []);
  const callSaveBaseshapePoints = (points) => {
    setParam(s => {s.baseshape[points.key] = points.position});
    const bbox = new THREE.Box3().setFromPoints(points.position);
    setParam(s => {s.baseshape.bbox = bbox});

  }
  // https://www.cresco.co.jp/blog/entry/22483/
  const callSavePointsWithIdx = useCallback((points) => {
    // if previous json data is loaded and there is stump length reduced, some comp points parameters are not updated
    // so we need to delete the previous data and load new data
    if (points.idx == 0) {
      let tempPoints = [];
      tempPoints.length = points.maxLength
      tempPoints[points.idx] = points.position
      setParam(s => {s.compress[points.key] = tempPoints});
      setParam(s => {s.compress.line_count = points.maxLength});
    } else {
      setParam(s => {s.compress[points.key][points.idx] = points.position});
    }
  }, []);

  const callSaveScannedCrossPoints = useCallback((points) => {
    setParam(s => {s.compress.scannedCrossPoints = points});
  }, []);

  const callSave2D = (points) => {
    setParam(s => {s.trim.vpt2D = points});
  }
  const callSaveMedialPoints2D = (points) => {
    setParam(s => {s.trim1.medialTrimPoints = points});
  }
  const callSaveLowerSurface = useCallback((surface) => {
    setControl(s => {s.meshTrim1LowerSurface = surface});
  }, []);
  const callSaveUpperPoints = useCallback((points) => {
    setParam(s => {s.trim2.upperCurvePointsAll = points});
  }, []);

  const callSaveUpperSurface = useCallback((surface) => {
    setControl(s => {s.meshTrim2UpperSurface = surface});
  }, []);

  const callSaveUpperSurfaceSi = useCallback((geometry) => {
    setControl(s => {s.meshTrim2UpperSurfaceSi = geometry});
  }, []);
  const callSaveUpperSurfaceMl = useCallback((surface) => {
    setControl(s => {s.meshTrim2UpperSurfaceMl = surface});
  }, []);
  const callSaveUpperSurfaceAp = useCallback((surface) => {
    setControl(s => {s.meshTrim2UpperSurfaceAp = surface});
  }, []);
  const callSaveUpperSurfaceMesh = useCallback((mesh) => {
    setParam(s => {s.trim3.upperSurfaceMesh = mesh});
  }, []);

  const callSaveAssembleSurface = useCallback((lowerSurface, upperSurface) => {
    setControl(s => {s.meshAssembleLowerSurface = lowerSurface});
    setControl(s => {s.meshAssembleUpperSurface = upperSurface});
  }, []);

  const callSaveOutputAk = useCallback((mesh) => {
    setControl(s => {s.meshOutputAk = mesh});
  }, []);

  

  return (
    <>
      { false    ? <MTest 
                      oc = {oc}
                    /> : null }

      { false     ? <MMesh
                    /> : null }


      { false     ? <MTrim3D
                      oc = {oc}
                      
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      />: null }

      { isMeasure  ? <MMeasure
                      angle = {currentAngle}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      /> : null }

      { isLeg     ? <MLeg 
                      meshLeg={meshLeg} 
                      clickable={flgLegClickable} 
                      showWire={wireLeg}
                      showTransparent={transparentLeg}
                      side={setup.side}
                      loadParam={setup.loadParam}
                      abductAngle = {comp.initialAbductionAngle}
                      flexAngle = {comp.initialFlexionAngle}
                      fileExt={0}
                      callback0={callSaveLegCenter}
                      callback1={callSaveLegRotation}
                    /> : null }
      
      { meshLeg && (activeStep == 1) ? <MBox 
                      mesh={meshLeg} 
                      color={'cyan'}
                      redrawBox={redrawBox}
                    /> : null }

      { isSocket     ? <MSocket
                      meshSocket={meshSocketShow} 
                      clickable={flgSocketClickable} 
                      opacity={opacity}
                      meshSocketThree={meshSocket}
                    /> : null }

      {/* display ischium level line */}
      { isIschium ? <MLine color={'deeppink'} axisMove={'y'} axisShow={'z'} isCenter={true} linewidth={3}/> : null }
      { isIschium ? <MLineMove color={'dodgerblue'} axisMove={'y'} axisShow={'z'} linewidth={1} /> : null }
      
      {/* display bottom length curve */}
      { isCenterX ? <MProjectCurve color={'deeppink'} axisMove={'x'} axisShow={'y'} initialPosition={align.stumpLengthX} linewidth={3} callback1={callSaveStumpLength} callback2={callSaveStumpLengthX} /> : null }
      { isCenterX ? <MLineMove color={'dodgerblue'} axisMove={'x'} axisShow={'y'} linewidth={1} /> : null }

      {/* display greater trochanter level line and point */}
      { isCenterY ? <MLine color={'deeppink'} axisMove={'z'} axisShow={'y'} linewidth={3} /> : null }
      { isCenterY ? <MLineMove color={'dodgerblue'} axisMove={'z'} axisShow={'y'} linewidth={1} /> : null }
      { isCenterY ? <MPointFix text={'Greater trochanter'} vpoint={align.greaterTrochanter} color={'deeppink'} isActive={isCenterY} callback={callSavePointMarkAk}/> : null }
      

      {/* base shape curve */}
      { isBaseShape ? <BaseShape
                      apDiameter={baseshape.apDiameter}
                      ischiumCircumference={baseshape.ischiumCircumference}
                      rectusFemorisOverhang={baseshape.rectusFemorisOverhang}
                      posteriorWallAngle={baseshape.posteriorWallAngle}
                      adjustmentLengthPosteriorWall={baseshape.adjustmentLengthPosteriorWall}
                      point1X={baseshape.point1X}
                      point1Z={baseshape.point1Z}
                      point3X={baseshape.point3X}
                      point3Z={baseshape.point3Z}
                      point4X={baseshape.point4X}
                      point4Z={baseshape.point4Z}
                      point5X={baseshape.point5X}
                      point5Z={baseshape.point5Z}
                      filletSideWall={baseshape.filletSideWall}
                      filletScalpa={baseshape.filletScalpa}
                      filletAdductorLongus={baseshape.filletAdductorLongus}
                      filletIschialTuberosity={baseshape.filletIschialTuberosity}
                      compression={baseshape.compression}
                      vlines={pointCross}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      no={comp.line_no}
                      callback={callSaveBaseshapePoints}
                    /> : null }

      { isCrossCurve && setup.akModeling == 0 ? <CrossSectionsAk
                      oc = {oc}
                      activeStep = {activeStep}
                      side = {setup.side}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust}
                      basePoints = {baseshape.points}
                      measuredCircumferenceLength = {baseshape.measuredCurveLength}
                      circumferenceLength = {baseshape.compresedCurveLength}
                      centroidBaseshape = {baseshape.centroid}
                      baseshapeCompression = {baseshape.compression}
                      bottomManualCircumference = {comp.bottomManualCircumference}
                      bottomRatioXY = {comp.bottomRatioXY}
                      secondBottomManualCircumference = {comp.secondBottomManualCircumference}
                      secondBottomRatioXY = {comp.secondBottomRatioXY}
                      bottomExtensionCurveRadius = {comp.bottomExtensionCurveRadius}
                      bottomExtension = {comp.bottomExtensionAk}
                      initialAdductionAngle = {-comp.initialAbductionAngle}
                      initialFlexionAngle = {comp.initialFlexionAngle}
                      line_no = {comp.line_no}
                      manualCircumference = {comp.manualCircumference}
                      tweenFactor = {comp.tweenFactor}
                      intervalAk = {comp.intervalAk}
                      compression = {comp.compression}
                      p0Y = {trim1.p0Y}
                      p0Z = {trim1.p0Z}
                      p1Y = {trim1.p1Y}
                      p1Z = {trim1.p1Z}
                      p2Y = {trim1.p2Y}
                      p2Z = {trim1.p2Z}
                      p3Y = {trim1.p3Y}
                      p3Z = {trim1.p3Z}

                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      callback={callSavePointsWithIdx}
                      callback2={callSaveLowerSurface}
                    /> : null }

      { isCrossCurve && setup.akModeling == 1 ? <CrossSectionsAkScan
                      activeStep = {activeStep}
                      oriMeshLeg={meshLegAligned}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust}
                      basePoints = {baseshape.points}
                      centroidBaseshape = {baseshape.centroid}
                      interval={comp.intervalAk}
                      smoothness={comp.smoothness}
                      bottomExtensionCurveRadius = {comp.bottomExtensionCurveRadius}
                      bottomExtension = {comp.bottomExtensionAk}
                      initialAdductionAngle = {-comp.initialAbductionAngle}
                      initialFlexionAngle = {comp.initialFlexionAngle}
                      line_no = {comp.line_no}
                      compression = {comp.compression}
                      manualCircumference = {comp.manualCircumference}
                      manualOrScan = {comp.manualOrScan}
                      tweenFactor = {comp.tweenFactor}
                      p0Y = {trim1.p0Y}
                      p0Z = {trim1.p0Z}
                      p1Y = {trim1.p1Y}
                      p1Z = {trim1.p1Z}
                      p2Y = {trim1.p2Y}
                      p2Z = {trim1.p2Z}
                      p3Y = {trim1.p3Y}
                      p3Z = {trim1.p3Z}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      callback={callSavePointsWithIdx}
                      callback2={callSaveLowerSurface}
                      callback3={callSaveScannedCrossPoints}
                     /> : null }

      { isCrossCurve && setup.akModeling == 0 ? <CrossSectionAkText
                      activeStep = {activeStep}
                      angle = {currentAngle}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust}
                      interval = {comp.intervalAk}
                      centroidBaseshape = {baseshape.centroid}
                      points = {comp.points}
                      bottomExtensionCurveRadius = {comp.bottomExtensionCurveRadius}
                      bottomManualCircumference = {comp.bottomManualCircumference}
                      secondBottomManualCircumference = {comp.secondBottomManualCircumference}
                      measuredCircumferenceLength = {baseshape.measuredCurveLength}
                      manualCircumference = {comp.manualCircumference}
                      baseshapeCompression = {baseshape.compression}
                      compression = {comp.compression}
                    /> : null }

      { isCrossCurve && setup.akModeling == 1 ? <CrossSectionAkScanText
                      activeStep = {activeStep}
                      angle = {currentAngle}
                      interval = {comp.intervalAk}
                      centroidBaseshape = {baseshape.centroid}
                      points = {comp.points}
                      oriPoints = {comp.scannedCrossPoints}
                      baseshapeCompression = {baseshape.compression}
                      compression = {comp.compression}
                    /> : null }

      {/* { isTrim1  ? <MPoints2DAdd 
                      // vpoints={trim1.medialTrimPoints}
                      vpoints={[
                        new THREE.Vector3(0, trim1.p0Y, trim1.p0Z),
                        new THREE.Vector3(0, trim1.p1Y, trim1.p1Z),
                        new THREE.Vector3(0, trim1.p2Y, trim1.p2Z),
                        new THREE.Vector3(0, trim1.p3Y, trim1.p3Z),
                      ]}
                      colorLine={'cyan'} 
                      colorDrag={'red'} 
                      colorClick={'pink'}
                      size1={2}
                      size2={0.5}
                      showtype={'2D'} 
                      // callback={callSaveMedialPoints2D}
                    /> : null } */}

      { isTrim2 ? <MUpperCurve
                      oc = {oc}
                      centroidBaseshape = {baseshape.centroid}
                      crossPoints = {comp.points}
                      greaterTrochanter = {align.greaterTrochanter}

                      idxPointForm = {trim2.idxPoint}
                      p0X = {trim2.p0X}
                      p0Y = {trim2.p0Y}
                      p1X = {trim2.p1X}
                      p1Y = {trim2.p1Y}
                      p2X = {trim2.p2X}
                      p2Y = {trim2.p2Y}
                      p3Y = {trim2.p3Y}
                      p3CenterVecAmplify = {trim2.p3CenterVecAmplify}
                      p4Y = {trim2.p4Y}
                      p4CenterVecAmplify = {trim2.p4CenterVecAmplify}
                      p5Y = {trim2.p5Y}
                      p5CenterVecAmplify = {trim2.p5CenterVecAmplify}
                      p6Y = {trim2.p6Y}
                      p6Z = {trim2.p6Z}
                      p7Y = {trim2.p7Y}
                      p7Z = {trim2.p7Z}
                      p8Y = {trim2.p8Y}
                      p8Z = {trim2.p8Z}
                      p9Y = {trim2.p9Y}
                      p9Z = {trim2.p9Z}
                      p10Y = {trim2.p10Y}
                      p10Z = {trim2.p10Z}
                      p11Y = {trim2.p11Y}
                      p11Z = {trim2.p11Z}
                      p12Y = {trim2.p12Y}
                      p12Z = {trim2.p12Z}
                      p13Y = {trim2.p13Y}
                      p13Z = {trim2.p13Z}
                      lowerSurface = {meshTrim1LowerSurface}

                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}

                      callback={callSaveUpperPoints}
                    /> : null }
      
      { isTrim2Si ? <MExtrudeSurface
                      vpoints={trim2.ptTrimSi}
                      show = {showTrimSurface} 
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      dirSurface={'y'}
                    /> : null }

      { isTrim2Ml ? <MExtrudeSurfaceWithPointIdx
                      vpoints={trim2.ptTrimMl}
                      show = {showTrimSurface} 
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      dirSurface={'x'}
                    /> : null }

      { isTrim2Ap ? <MExtrudeSurfaceWithPointIdx
                      vpoints={trim2.ptTrimAp}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      dirSurface={'z'}
                    /> : null }

      { isTrim2 && (trim2.upperCurvePointsAll != null) && (trim2.runTrimSi===false) ? <MUpperSurface
                      oc = {oc}
                      crossPoints = {comp.points}
                      lowerSurface = {meshTrim1LowerSurface}
                      centroidBaseshape = {baseshape.centroid}
                      upperCurvePointsAll = {trim2.upperCurvePointsAll}
                      idxPointForm = {trim2.idxPoint}
                      callback= {callSaveUpperSurface}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }

      { (isTrim2Si && (meshTrim2UpperSurface !== null) && trim2.runTrimSi ) || ((meshTrim2UpperSurfaceSi !== null) && trim2.runTrimSi && trim2.runTrimMl===false) ? <MUpperSurfaceSi
                      oc = {oc}
                      upperSurface = {meshTrim2UpperSurface}
                      pointsTrimSi = {trim2.ptTrimSi}
                      offsetPointsSi={new THREE.Vector3(trim2.trimSiX, 0, trim2.trimSiZ)}
                      callback={callSaveUpperSurfaceSi}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }
                    
      {/*
      { (isTrim2Ml && (trim2.upperSurfaceSi !== null) && trim2.runTrimMl) || ((trim2.upperSurfaceMl !== null) && trim2.runTrimMl && trim2.runTrimAp===false) ? <MUpperSurfaceMl
      */}
      { (isTrim2Ml && (meshTrim2UpperSurfaceSi !== null) && trim2.runTrimMl) ? <MUpperSurfaceMl
                      oc = {oc}
                      upperSurface = {meshTrim2UpperSurfaceSi}
                      pointsTrim = {trim2.ptTrimMl}
                      callback={callSaveUpperSurfaceMl}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }

      {/*
      { ((isTrim2Ap || isAssemble) && (trim2.upperSurfaceMl !== null) && trim2.runTrimAp) ? <MUpperSurfaceAp
      */}
      { ((isTrim2Ap) && (meshTrim2UpperSurfaceMl !== null) && trim2.runTrimAp) ? <MUpperSurfaceAp
                      oc = {oc}
                      upperSurface = {meshTrim2UpperSurfaceMl}
                      pointsTrim = {trim2.ptTrimAp}
                      callback={callSaveUpperSurfaceAp}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }

        
      { false && (meshTrim2UpperSurfaceAp !== null) ? <MTrim3D
                      oc = {oc}
                      centroidBaseshape = {baseshape.centroid}
                      upperSurfaceMesh = {meshTrim2UpperSurfaceAp}
                      basePoints = {comp.points[comp.points.length-1]}
                      callback={callSaveUpperSurfaceMesh}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                      />: null }
      

      { isAssemble ? <MAssembleAk
                      activeStep = {activeStep}
                      side={setup.side}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust + comp.bottomExtensionAk}
                      centroidBaseshape = {baseshape.centroid}
                      oriLowerSurface = {meshTrim1LowerSurface}
                      oriUpperSurface = {meshTrim2UpperSurfaceMl}
                      assembleAdapter = {assembleAk.adapter}
                      assembleSocketMl = {assembleAk.socketMl}
                      assembleSocketAp = {assembleAk.socketAp}
                      assembleSocketSi = {assembleAk.socketSi}
                      assembleFlexionExtension = {assembleAk.flexionExtension}
                      assembleAdductionAbduction = {assembleAk.adductionAbduction}
                      assembleSocketRotation = {assembleAk.socketRotation}
                      meshFoot = {meshFoot}
                      meshKnee = {meshKnee}
                      callback = {callSaveAssembleSurface}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }

      {/* { isOutput ? <MOutputAkFd
                      type={type}
                      side={setup.side}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust + comp.bottomExtensionAk}
                      apDiameter={baseshape.apDiameter}
                      centroidBaseshape = {baseshape.centroid}
                      crossPoints = {comp.points}
                      adductAngle = {-comp.initialAbductionAngle}
                      flexAngle = {comp.initialFlexionAngle}
                      oriLowerSurface = {meshTrim1LowerSurface} 
                      oriUpperSurface = {meshTrim2UpperSurfaceMl}
                      socketThickness = {output.socketThickness}
                      radiusCheckHole = {output.radiusCheckHole}
                      intervalCheckHole = {output.intervalCheckHole}
                      stepFlow = {output.step}
                      splitAdjust = {output.splitAdjust}
                      assembleFlexionExtension = {assembleAk.flexionExtension}
                      assembleAdductionAbduction = {assembleAk.adductionAbduction}
                      assembleSocketRotation = {assembleAk.socketRotation}
                      callback = {callSaveOutputAk}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null } */}


      { isOutputFd || isOutput ? <MOutputAkFd
                      type={type}
                      side={setup.side}
                      stumpLength = {align.stumpLength + align.stumpLengthAdjust + comp.bottomExtensionAk}
                      apDiameter={baseshape.apDiameter}
                      centroidBaseshape = {baseshape.centroid}
                      crossPoints = {comp.points}
                      adductAngle = {-comp.initialAbductionAngle}
                      flexAngle = {comp.initialFlexionAngle}
                      oriLowerSurface = {meshTrim1LowerSurface}
                      oriUpperSurface = {meshTrim2UpperSurfaceMl}
                      socketThickness = {isOutputFd ? output.socketThicknessFd : output.socketThickness}
                      radiusCheckHole = {output.radiusCheckHole}
                      intervalCheckHole = {output.intervalCheckHole}
                      stepFlow = {output.step}
                      splitAdjust = {output.splitAdjust}
                      assembleAdapter = {assembleAk.adapter}
                      assembleSocketMl = {assembleAk.socketMl}
                      assembleSocketAp = {assembleAk.socketAp}
                      assembleSocketSi = {assembleAk.socketSi}
                      assembleFlexionExtension = {assembleAk.flexionExtension}
                      assembleAdductionAbduction = {assembleAk.adductionAbduction}
                      assembleSocketRotation = {assembleAk.socketRotation}
                      engraving = {output.engraving}
                      callback = {callSaveOutputAk}
                      color={'dodgerblue'}
                      colorHigh={'deeppink'}
                      colorSub={'orange'}
                    /> : null }
    </>
  );
};

export default MarkingCommon;
